import { useState, useEffect } from "react";

interface MobileDetectionResult {
  isMobile: boolean;
  isSmallScreen: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isTablet: boolean;
  hasTouchScreen: boolean;
}

/**
 * Hook for detecting mobile devices, screen sizes, and orientations
 * @returns Object containing device and viewport information
 */
export function useMobile(): MobileDetectionResult {
  const [state, setState] = useState<MobileDetectionResult>({
    isMobile: false,
    isSmallScreen: false,
    isPortrait: true,
    isLandscape: false,
    isIOS: false,
    isAndroid: false,
    isTablet: false,
    hasTouchScreen: false,
  });

  useEffect(() => {
    const checkDevice = () => {
      // Check for touch device
      const hasTouchScreen = (
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0
      );

      // Check screen size
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const isSmallScreen = windowWidth < 640;
      
      // Check orientation
      const isPortrait = windowHeight > windowWidth;
      const isLandscape = !isPortrait;

      // Check for mobile/tablet using user agent
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      
      // Check for iOS
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
      
      // Check for Android
      const isAndroid = /android/i.test(userAgent);
      
      // Generally determine if mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Check if tablet (larger screen but still mobile OS)
      const isTablet = (isMobile && Math.min(windowWidth, windowHeight) > 480) || /iPad/.test(userAgent);

      setState({
        isMobile,
        isSmallScreen,
        isPortrait,
        isLandscape,
        isIOS,
        isAndroid,
        isTablet,
        hasTouchScreen,
      });
    };

    // Initial check
    checkDevice();

    // Add resize listener to handle orientation changes and window size changes
    window.addEventListener('resize', checkDevice);

    // Add orientation change listener for mobile devices
    window.addEventListener('orientationchange', checkDevice);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  return state;
}

export default useMobile;
